import React from 'react';

import { check, upgrade } from '@/assets/icons';
import { Video } from '@/config/constants';
import { timeFormatter } from '@/helpers/timeFormatter';
import { useSubscription } from '@/providers/SubscriptionProvider';
import { ButtonBase, SvgIcon } from '@material-ui/core';
import { isNil } from 'ramda';
import { Link } from 'react-router-dom';

import { AsaphButton } from '../Button';
import { S3ImageWrapper } from '../S3ImageWrapper/S3ImageWrapper';
import styles from './ContentCard.module.scss';
import { LinearProgressIndicator } from './LinearProgressIndicator';

interface Video {
	description: string;
	duration: number;
	fullyWatched?: boolean;
	id: string;
	name: string;
	paid: boolean;
	percentagePlayed?: number;
	thumbnailUrl: string;
	url?: string | undefined | null;
}

export interface ContentCardProps {
	video: Video;
	onClick?: () => void;
	link?: string;
	outlined?: boolean;
	lightSkeleton?: boolean;
	suppressPaidContentModal?: boolean;
}

export const ContentCard: React.FC<ContentCardProps> = (props) => {
	const { status, setPaidModalOpen } = useSubscription();
	const isPaidContent = status ? false : props.video.paid;

	let percentagePlayedComponent;
	if (
		!isNil(props.video.percentagePlayed) &&
		props.video.percentagePlayed < 1
	) {
		percentagePlayedComponent = (
			<LinearProgressIndicator
				percentagePlayed={props.video.percentagePlayed * 100}
			/>
		);
	}
	if (!!props.video.fullyWatched) {
		percentagePlayedComponent = (
			<>
				<div className={styles['content-card__icon-container']}>
					<SvgIcon
						component={check}
						color="primary"
						classes={{ colorPrimary: styles['content-card__icon'] }}
					/>
				</div>
				{percentagePlayedComponent}
			</>
		);
	}
	if (isPaidContent) {
		percentagePlayedComponent = null;
	}

	const contentCardClasses = [
		styles['content-card'],
	];

	const contentCardContentClasses = [
		styles['content-card__content'],
		props.outlined ? styles['content-card__content--outlined'] : '',
	];

	return (
		<div className={contentCardClasses.join(' ')}>
			<div className={styles['content-card__image']}>
				{/* <S3ImageWrapper
					imgKey={props.video.poster.key}
					lightSkeleton={props.lightSkeleton}
				/> */}
				<img src={props.video.thumbnailUrl} />
			</div>
			<div className={contentCardContentClasses.join(' ')}>
				<div className={styles['content-card__top']}>
					<div className={styles['content-card__top-left']}>
						{percentagePlayedComponent}
					</div>
					<div className={styles['content-card__duration']}>
						{timeFormatter(props.video.duration)}
					</div>
				</div>

				<span className={styles['content-card__name']}>
					{props.video.name}
				</span>

				{isPaidContent && !props.suppressPaidContentModal && (
					<div
						className={styles['content-card__paid-content-wrapper']}
					>
						<AsaphButton
							color="blue"
							label="Upgrade"
							icon={
								<SvgIcon
									component={upgrade}
									color="disabled"
									fontSize="inherit"
								/>
							}
							className={styles['content-card__paid-content']}
						/>
					</div>
				)}
			</div>
			{isPaidContent && !props.suppressPaidContentModal && (
				<ButtonBase
					focusRipple
					className={styles['content-card__button']}
					onClick={() => setPaidModalOpen(true)}
					title={props.video.name}
				/>
			)}
			{(!isPaidContent || props.suppressPaidContentModal) &&
				!!props.onClick && (
					<ButtonBase
						focusRipple
						className={styles['content-card__button']}
						onClick={props.onClick}
						title={props.video.name}
					/>
				)}
			{(!isPaidContent || props.suppressPaidContentModal) &&
				!!props.link && (
					<ButtonBase
						focusRipple
						className={styles['content-card__button']}
						component={Link}
						to={props.link}
						title={props.video.name}
					/>
				)}
		</div>
	);
};
